// =============================================================================
// LP Express — Problem / Solution (PAS / unique-mechanism) template renderer.
//
// Renders the SECTION-BASED problem-solution page (see
// shared/lp-express-problem-solution.ts + shared/lp-express-sections.ts) as a
// long-scrolling PAS sales page, ported from the template library's
// sales.problem-solution: problem-framing hero → problem agitation → "why the
// usual fixes fail" cards → the unique-mechanism reveal (with an AI explainer
// diagram) → the solution reveal ("Meet [product]" + benefits + the brand's
// REAL product photo) → proof quotes (swappable for REAL reviews via the
// shared review picker) → bordered offer box → guarantee → FAQ accordion →
// legal disclaimer → sticky bottom CTA bar (mobile).
//
// The component renders ONE section block per invocation (selected via the
// `section` prop), so the page flows through the SAME ordered sections
// pipeline as the product-hero: getLpSections order, reorder, per-section
// hide/delete, and per-section copy regeneration. Copy is read via
// `assembleLpProblemSolutionContent(generatedContent)` (which reads the
// per-section keys — psHero, psProblem, …) and edited in place through the
// SAME path-based `setText` ops the other templates use (e.g.
// ["psWhyFail","items",0,"title"]).
//
//  • Imagery: the mechanism section shows ONE AI diagram slot
//    (generatedContent.problemSolutionImages, key "mechanism-diagram") with a
//    per-slot spinner keyed on its OWN status (never an aggregate flag),
//    retry-on-failed in the builder, and swap/upload via the media picker.
//    The solution section shows the brand's REAL product photo (override key
//    "psSolution:product-photo", swappable via the picker/upload, never
//    AI-generated).
//  • Prices, savings and discount framing are ALWAYS derived from the real
//    product/offer/sale data (ctx.receipt) — the generated copy never carries
//    dollar amounts, and strikethrough only appears when a real active
//    discount exists on the run.
//  • Brand adaptation comes from the shared --color-brand-* CSS variables the
//    renderer already sets (brandCssVarsV2).
// =============================================================================
import { useState } from "react";
import {
  Check,
  Heart,
  Leaf,
  Loader2,
  Plus,
  RotateCw,
  ShieldCheck,
  Sparkles,
  Star,
  Truck,
  X,
  Zap,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  useLpEditor,
  LpImageActions,
  LpReviewActions,
  useLpMediaOverridesMap,
  useLpReviewOverride,
  type LpReviewChoice,
} from "@/components/lp-editor";
import type { Product } from "@shared/schema";
import type { LpReceipt, LpReviewDisplay } from "@/lib/lp-express-pricing";
import type { LpSectionType } from "@shared/lp-express-sections";
import {
  assembleLpProblemSolutionContent,
  type LpProblemSolutionContent,
} from "@shared/lp-express-problem-solution";

export type ProblemSolutionCtx = {
  brand: any;
  offer: { checkoutUrl?: string } | any;
  generatedContent: any;
  logoUrl: string | null;
  bundleProductPhotos: string[];
  bundleImage?: string | null;
  products?: Product[];
  receipt?: LpReceipt;
  reviewDisplay?: LpReviewDisplay;
  typography?: { heading?: string; body?: string } | any;
  /** In-app builder only — retries/regenerates the AI mechanism-diagram slot
   *  (generatedContent.problemSolutionImages). Undefined on the public page,
   *  so failed slots stay hidden there (no retry affordance). */
  onRetrySlot?: (mergeKey: "problemSolutionImages", slotKey: string) => void;
};

// Shared brand palette (same CSS vars the quiz/listicle/advertorial/
// product-hero templates use, set by the renderer root via brandCssVarsV2).
const HEADING = "var(--color-brand-navy)";
const ACCENT = "var(--color-brand-green)";
const DEEP = "var(--color-brand-deepnavy)";
const TEXT = "var(--color-brand-text)";
const MUTED = "var(--color-brand-muted)";
const CARD = "var(--color-brand-card)";
const BORDER = "var(--color-brand-lightborder)";
const CORAL = "var(--color-brand-coral)";
const PAGE_BG = "#FFFFFF";
const SOFT = "#F7F5EF";

// Media-override keys. The mechanism diagram is an AI slot (regenerable); the
// solution product image is a REAL product-photo slot (picker/upload only).
const MECHANISM_DIAGRAM_AI_PREFIX = "psMechanism:diagram-ai-";
const mechanismAiSlotKey = (slotKey: string): string =>
  `${MECHANISM_DIAGRAM_AI_PREFIX}${slotKey}`;
const SOLUTION_PHOTO_SLOT = "psSolution:product-photo";

// Curated lucide icon set for the benefits grid — replaces any workflow `ic`
// hint (house style is lucide icons, never emoji).
const BENEFIT_ICONS = [Zap, ShieldCheck, Leaf, Sparkles, Heart, Truck];

function starRow(rating: number, size = 14) {
  return (
    <span style={{ display: "inline-flex", color: "#F5A623" }}>
      {Array.from({ length: Math.max(1, Math.min(5, rating)) }).map((_, s) => (
        <Star key={s} style={{ width: size, height: size }} className="fill-current" />
      ))}
    </span>
  );
}

// ── CTA button ───────────────────────────────────────────────────────────────
// Editor / no-URL → non-navigating span so copy stays editable.
function CtaButton({
  label,
  path,
  href,
  testId,
  variant = "solid",
}: {
  label: string;
  path: (string | number)[];
  href?: string;
  testId: string;
  variant?: "solid" | "onDark";
}) {
  const editor = useLpEditor();
  const inner = (
    <EditableText
      value={label}
      path={path}
      as="span"
      noHideDelete
      placeholder="Get started"
      testId={`${testId}-label`}
    />
  );
  const style: React.CSSProperties = {
    display: "inline-block",
    padding: "16px 44px",
    backgroundColor: variant === "onDark" ? "#FFFFFF" : ACCENT,
    color: variant === "onDark" ? DEEP : "#FFFFFF",
    fontSize: 17,
    fontWeight: 800,
    textAlign: "center",
    borderRadius: 12,
    letterSpacing: "0.02em",
    textDecoration: "none",
    boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
    cursor: "pointer",
  };
  if (editor || !href) {
    return (
      <span
        style={{ ...style, ...(editor ? {} : { opacity: 0.7, cursor: "not-allowed" }) }}
        data-testid={testId}
        data-lp-cta
      >
        {inner}
      </span>
    );
  }
  return (
    <a href={href} style={style} data-testid={testId} data-lp-cta>
      {inner}
    </a>
  );
}

// ── Section heading helper ───────────────────────────────────────────────────
function SectionHeading({
  value,
  path,
  headingFont,
  placeholder,
  testId,
  color = HEADING,
}: {
  value: string;
  path: (string | number)[];
  headingFont: string;
  placeholder: string;
  testId: string;
  color?: string;
}) {
  return (
    <EditableText
      value={value}
      path={path}
      as="h2"
      className={headingFont}
      style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1.25, margin: "0 0 8px", textAlign: "center", letterSpacing: "-0.01em" }}
      placeholder={placeholder}
      testId={testId}
    />
  );
}

// ── Hero (problem-framing) ───────────────────────────────────────────────────
function HeroSection({ content, ctx, headingFont, editor }: { content: LpProblemSolutionContent; ctx: ProblemSolutionCtx; headingFont: string; editor: boolean }) {
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  return (
    <div style={{ backgroundColor: PAGE_BG }} data-testid="problem-solution-page">
      {/* Top nav: brand logo/name + shop-now CTA */}
      <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "14px 16px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {ctx.logoUrl && <img src={ctx.logoUrl} alt="" style={{ width: 34, height: 34, objectFit: "contain" }} data-testid="img-problem-solution-logo" />}
            <span style={{ fontSize: 17, fontWeight: 800, color: HEADING }}>{ctx.brand?.name ?? ""}</span>
          </div>
          {!editor && checkoutUrl && (
            <a
              href={checkoutUrl}
              style={{ fontSize: 13, fontWeight: 800, color: "#fff", backgroundColor: ACCENT, padding: "9px 18px", borderRadius: 9999, textDecoration: "none" }}
              data-testid="link-problem-solution-nav-cta"
              data-lp-cta
            >
              {content.offer.ctaText || "Shop now"}
            </a>
          )}
        </div>
      </div>

      <div style={{ padding: "64px 20px 56px" }} data-testid="problem-solution-hero">
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          {(content.eyebrow || editor) && (
            <EditableText
              value={content.eyebrow}
              path={["psHero", "eyebrow"]}
              as="p"
              className="uppercase"
              style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: ACCENT, margin: "0 0 14px" }}
              placeholder="If nothing has worked, read this"
              testId="text-problem-solution-eyebrow"
            />
          )}
          <EditableText
            value={content.headline}
            path={["psHero", "headline"]}
            as="h1"
            className={headingFont}
            multiline
            style={{ fontSize: 40, fontWeight: 900, color: HEADING, lineHeight: 1.12, margin: "0 0 16px", letterSpacing: "-0.02em" }}
            placeholder="Name the problem, not the product"
            testId="text-problem-solution-headline"
          />
          {(content.subhead || editor) && (
            <EditableText
              value={content.subhead}
              path={["psHero", "subhead"]}
              as="p"
              multiline
              style={{ fontSize: 18, lineHeight: 1.6, color: MUTED, margin: "0 auto", maxWidth: 620 }}
              placeholder="It's not what you think — it's the mechanism underneath"
              testId="text-problem-solution-subhead"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Problem agitation ────────────────────────────────────────────────────────
function ProblemSection({ content, headingFont, editor }: { content: LpProblemSolutionContent; headingFont: string; editor: boolean }) {
  if (!content.problem.heading && content.problem.paragraphs.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, padding: "56px 20px" }} data-testid="problem-solution-problem">
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <SectionHeading
          value={content.problem.heading}
          path={["psProblem", "heading"]}
          headingFont={headingFont}
          placeholder="Sound familiar?"
          testId="text-problem-solution-problem-heading"
        />
        <div style={{ marginTop: 20 }}>
          {content.problem.paragraphs.map((p, i) => (
            <EditableText
              key={i}
              value={p}
              path={["psProblem", "paragraphs", i]}
              as="p"
              multiline
              style={{ fontSize: 17, lineHeight: 1.75, color: TEXT, margin: "0 0 18px" }}
              placeholder="Agitation paragraph"
              testId={`text-problem-solution-problem-p-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Why the usual fixes fail ─────────────────────────────────────────────────
function WhyFailSection({ content, headingFont, editor }: { content: LpProblemSolutionContent; headingFont: string; editor: boolean }) {
  if (content.whyFail.items.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, padding: "56px 20px" }} data-testid="problem-solution-whyfail">
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SectionHeading
          value={content.whyFail.heading}
          path={["psWhyFail", "heading"]}
          headingFont={headingFont}
          placeholder="Why the usual fixes don't work"
          testId="text-problem-solution-whyfail-heading"
        />
        <div style={{ marginTop: 26 }}>
          {content.whyFail.items.map((it, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "flex-start", gap: 14, backgroundColor: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px", marginBottom: 14 }}
              data-testid={`problem-solution-whyfail-${i}`}
            >
              <span style={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: "rgba(224,90,90,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                <X style={{ width: 16, height: 16, color: CORAL }} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <EditableText
                  value={it.title}
                  path={["psWhyFail", "items", i, "title"]}
                  as="h3"
                  className={headingFont}
                  style={{ fontSize: 17, fontWeight: 800, color: HEADING, margin: "0 0 4px" }}
                  placeholder="Common fix"
                  testId={`text-problem-solution-whyfail-title-${i}`}
                />
                <EditableText
                  value={it.why}
                  path={["psWhyFail", "items", i, "why"]}
                  as="p"
                  multiline
                  style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: 0 }}
                  placeholder="Why it doesn't touch the mechanism"
                  testId={`text-problem-solution-whyfail-why-${i}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Mechanism (with the AI diagram slot) ─────────────────────────────────────
// The diagram is the page's single AI image slot. Its spinner/retry state keys
// off its OWN slot status in generatedContent.problemSolutionImages — never an
// aggregate loading flag. Pending/failed slots render only in the BUILDER
// (onRetrySlot present); the public page is a static snapshot with no polling,
// so slots without a URL simply don't render there.
function MechanismDiagram({ ctx }: { ctx: ProblemSolutionCtx }) {
  const overrides = useLpMediaOverridesMap();
  const editor = useLpEditor();
  const raw: any[] = Array.isArray(ctx.generatedContent?.problemSolutionImages)
    ? ctx.generatedContent.problemSolutionImages
    : [];
  const slot = raw.find((s: any) => s && s.key === "mechanism-diagram");
  if (!slot) return null;
  const canRetry = !!(editor && ctx.onRetrySlot);
  const url =
    (overrides?.[mechanismAiSlotKey(slot.key)]?.url as string | undefined) ??
    (slot.imageStatus === "failed" ? null : (slot.imageUrl as string | null)) ??
    null;
  const status = slot.imageStatus as string | undefined;
  if (!url && !(status === "pending" && !!ctx.onRetrySlot) && !(status === "failed" && canRetry)) {
    return null;
  }
  return (
    <div className="group/lpimg relative" style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${BORDER}`, backgroundColor: "#FFFFFF", marginTop: 28 }}>
      {url ? (
        <img
          src={url}
          alt={(slot.altText as string | undefined) || ""}
          style={{ display: "block", width: "100%", maxHeight: 420, objectFit: "cover" }}
          data-testid="img-problem-solution-diagram"
        />
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, height: 280, backgroundColor: SOFT }}
          data-testid="img-problem-solution-diagram"
        >
          {status === "failed" ? (
            canRetry ? (
              <button
                type="button"
                onClick={() => ctx.onRetrySlot?.("problemSolutionImages", slot.key)}
                className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold"
                style={{ color: HEADING, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}
                data-testid={`button-retry-problem-solution-ai-${slot.key}`}
              >
                <RotateCw className="h-3.5 w-3.5" /> Retry diagram
              </button>
            ) : (
              <Sparkles className="h-5 w-5" style={{ color: MUTED }} />
            )
          ) : (
            <>
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: MUTED }} />
              <span style={{ fontSize: 12, color: MUTED }}>Generating diagram…</span>
            </>
          )}
        </div>
      )}
      {url && (
        <LpImageActions
          slotKey={mechanismAiSlotKey(slot.key)}
          canRegenerate
          onRegenerate={
            ctx.onRetrySlot
              ? () => ctx.onRetrySlot?.("problemSolutionImages", slot.key)
              : undefined
          }
          productPhotos={ctx.bundleProductPhotos}
          revertLabel="Use default"
          revertTitle="Revert to the AI diagram"
          testIdPrefix={`problem-solution-ai-slot-${slot.key}`}
        />
      )}
    </div>
  );
}

function MechanismSection({ content, ctx, headingFont, editor }: { content: LpProblemSolutionContent; ctx: ProblemSolutionCtx; headingFont: string; editor: boolean }) {
  if (!content.mechanism.heading && content.mechanism.paragraphs.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, padding: "56px 20px" }} data-testid="problem-solution-mechanism">
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <p className="uppercase" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: ACCENT, margin: "0 0 10px", textAlign: "center" }}>
          The real reason
        </p>
        <SectionHeading
          value={content.mechanism.heading}
          path={["psMechanism", "heading"]}
          headingFont={headingFont}
          placeholder="The mechanism nobody talks about"
          testId="text-problem-solution-mechanism-heading"
        />
        <div style={{ marginTop: 20 }}>
          {content.mechanism.paragraphs.map((p, i) => (
            <EditableText
              key={i}
              value={p}
              path={["psMechanism", "paragraphs", i]}
              as="p"
              multiline
              style={{ fontSize: 17, lineHeight: 1.75, color: TEXT, margin: "0 0 18px" }}
              placeholder="Mechanism paragraph"
              testId={`text-problem-solution-mechanism-p-${i}`}
            />
          ))}
        </div>
        <MechanismDiagram ctx={ctx} />
      </div>
    </div>
  );
}

// ── Solution reveal (REAL product photo + benefits grid) ─────────────────────
function SolutionSection({ content, ctx, headingFont, editor }: { content: LpProblemSolutionContent; ctx: ProblemSolutionCtx; headingFont: string; editor: boolean }) {
  const overrides = useLpMediaOverridesMap();
  const photos =
    ctx.bundleProductPhotos.length > 0
      ? ctx.bundleProductPhotos
      : ctx.bundleImage
        ? [ctx.bundleImage]
        : [];
  const photo = (overrides?.[SOLUTION_PHOTO_SLOT]?.url as string | undefined) ?? photos[0] ?? null;
  if (!content.solution.heading && content.solution.benefits.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, padding: "64px 20px" }} data-testid="problem-solution-solution">
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: 44, alignItems: "center" }}>
          {/* REAL product photo — swap via the media picker only, never AI. */}
          <div className="group/lpimg relative" style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${BORDER}`, backgroundColor: SOFT }}>
            {photo ? (
              <img
                src={photo}
                alt=""
                style={{ display: "block", width: "100%", height: 400, objectFit: "contain", padding: 20 }}
                data-testid="img-problem-solution-product"
              />
            ) : (
              <div style={{ height: 400 }} data-testid="img-problem-solution-product" />
            )}
            <LpImageActions
              slotKey={SOLUTION_PHOTO_SLOT}
              canRegenerate={false}
              productPhotos={ctx.bundleProductPhotos}
              revertLabel="Use default"
              revertTitle="Revert to the default product photo"
              testIdPrefix="problem-solution-product-photo"
            />
          </div>
          <div>
            <p className="uppercase" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: ACCENT, margin: "0 0 10px" }}>
              The solution
            </p>
            <EditableText
              value={content.solution.heading}
              path={["psSolution", "heading"]}
              as="h2"
              className={headingFont}
              style={{ fontSize: 30, fontWeight: 900, color: HEADING, lineHeight: 1.2, margin: "0 0 14px", letterSpacing: "-0.01em" }}
              placeholder="Meet the product"
              testId="text-problem-solution-solution-heading"
            />
            {content.solution.paragraphs.map((p, i) => (
              <EditableText
                key={i}
                value={p}
                path={["psSolution", "paragraphs", i]}
                as="p"
                multiline
                style={{ fontSize: 16, lineHeight: 1.7, color: TEXT, margin: "0 0 14px" }}
                placeholder="Solution paragraph"
                testId={`text-problem-solution-solution-p-${i}`}
              />
            ))}
          </div>
        </div>
        {(content.solution.benefits.length > 0 || editor) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: 22, marginTop: 44 }}>
            {content.solution.benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
              return (
                <div
                  key={i}
                  style={{ backgroundColor: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 22px" }}
                  data-testid={`problem-solution-benefit-${i}`}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "rgba(45,122,79,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                    <Icon style={{ width: 22, height: 22, color: ACCENT }} />
                  </div>
                  <EditableText
                    value={b.title}
                    path={["psSolution", "benefits", i, "title"]}
                    as="h3"
                    className={headingFont}
                    style={{ fontSize: 18, fontWeight: 800, color: HEADING, margin: "0 0 8px" }}
                    placeholder="Benefit title"
                    testId={`text-problem-solution-benefit-title-${i}`}
                  />
                  <EditableText
                    value={b.body}
                    path={["psSolution", "benefits", i, "body"]}
                    as="p"
                    multiline
                    style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: 0 }}
                    placeholder="Benefit body"
                    testId={`text-problem-solution-benefit-body-${i}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Proof quotes (real reviews win via the shared review picker) ────────────
function QuoteCard({
  slot,
  item,
  options,
}: {
  slot: number;
  item?: { name: string; role: string; text: string; rating: number };
  options: LpReviewChoice[];
}) {
  const editor = useLpEditor();
  const slotKey = `psProof:quotes:${slot}`;
  const override = useLpReviewOverride(slotKey);
  const rev: LpReviewChoice | null = override ?? options[slot] ?? null;
  const AVATARS = ["#E8927C", "#7BBFB2", "#A78BCA", "#E0A45E", "#8CB4D9", "#C4A265"];
  if (!rev && !item && !editor) return null;
  const name = rev ? rev.reviewer || "Verified customer" : item?.name ?? "";
  const rating = rev ? rev.rating : item?.rating ?? 5;
  const base: (string | number)[] = ["psProof", "items", slot];
  return (
    <div
      className="group/lpreview relative"
      style={{ backgroundColor: CARD, borderRadius: 14, border: `1px solid ${BORDER}`, padding: "18px 20px" }}
      data-testid={`problem-solution-quote-${slot}`}
    >
      {options.length > 0 && (
        <LpReviewActions slotKey={slotKey} reviews={options} testIdPrefix={`problem-solution-quote-${slot}`} />
      )}
      <div style={{ marginBottom: 8 }}>{starRow(rating, 13)}</div>
      {rev ? (
        <p style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: "0 0 12px" }}>"{rev.reviewText}"</p>
      ) : (
        <EditableText
          value={item?.text ?? ""}
          path={[...base, "text"]}
          as="p"
          multiline
          style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: "0 0 12px" }}
          placeholder="Testimonial quote"
          testId={`text-problem-solution-quote-text-${slot}`}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <div
          style={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: AVATARS[slot % AVATARS.length], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          <span style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{(name || "A")[0]}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          {rev ? (
            <p style={{ fontSize: 13, fontWeight: 700, color: HEADING, margin: 0 }}>{name}</p>
          ) : (
            <EditableText
              value={item?.name ?? ""}
              path={[...base, "name"]}
              as="p"
              style={{ fontSize: 13, fontWeight: 700, color: HEADING, margin: 0 }}
              placeholder="Customer name"
              testId={`text-problem-solution-quote-name-${slot}`}
            />
          )}
          {!rev && (item?.role || editor) && (
            <EditableText
              value={item?.role ?? ""}
              path={[...base, "role"]}
              as="p"
              style={{ fontSize: 11, color: MUTED, margin: 0 }}
              placeholder="Verified buyer"
              testId={`text-problem-solution-quote-role-${slot}`}
            />
          )}
          {rev && <p style={{ fontSize: 11, color: ACCENT, margin: 0, fontWeight: 600 }}>Verified buyer</p>}
        </div>
      </div>
    </div>
  );
}

function ProofSection({ content, ctx, headingFont }: { content: LpProblemSolutionContent; ctx: ProblemSolutionCtx; headingFont: string }) {
  const reviewDisplay = ctx.reviewDisplay;
  const reviewOptions = (reviewDisplay?.allReviews ?? []) as LpReviewChoice[];
  const quoteItems = Array.isArray(content.proof.items) ? content.proof.items : [];
  if (quoteItems.length === 0 && reviewOptions.length === 0) return null;
  return (
    <EditableElement elementKey="problem-solution-proof" toolbarSide="left">
      <div style={{ backgroundColor: SOFT, padding: "56px 20px" }} data-testid="problem-solution-proof">
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <SectionHeading
            value={content.proof.heading}
            path={["psProof", "heading"]}
            headingFont={headingFont}
            placeholder="What customers are saying"
            testId="text-problem-solution-proof-heading"
          />
          {reviewDisplay?.hasReviews && Array.isArray(content.proof.stats) && content.proof.stats.length > 0 && (
            <p style={{ fontSize: 15, color: MUTED, textAlign: "center", margin: "0 0 8px" }} data-testid="text-problem-solution-proof-agg">
              <span style={{ color: "#F5A623" }}>★★★★★</span> {reviewDisplay.ratingText} · {reviewDisplay.reviewCountText} reviews
            </p>
          )}
          {/* Stat cards (source template proof.stats). FTC-safe: stored stats
              are operator-entered/edited (the workflow never invents numbers);
              when none are stored, stats are DERIVED from the brand's REAL
              review aggregates; hidden entirely when neither exists. */}
          {(() => {
            const storedStats = Array.isArray(content.proof.stats) ? content.proof.stats : [];
            const derivedStats =
              storedStats.length === 0 && reviewDisplay?.hasReviews
                ? [
                    { n: `${reviewDisplay.ratingText}★`, l: "average rating" },
                    { n: reviewDisplay.reviewCountText, l: "customer reviews" },
                  ]
                : [];
            const stats = storedStats.length > 0 ? storedStats : derivedStats;
            if (stats.length === 0) return null;
            const editableStored = storedStats.length > 0;
            // Size the grid to the ACTUAL card count so a 2-stat band (the
            // derived-from-reviews case) sits centered instead of filling the
            // left half of a fixed 4-column track. Literal class names so
            // Tailwind sees them.
            const shown = stats.slice(0, 4);
            const desktopCols =
              shown.length >= 4
                ? "md:grid-cols-4"
                : shown.length === 3
                  ? "md:grid-cols-3"
                  : "";
            const gridMaxWidth = Math.min(720, shown.length * 180 + (shown.length - 1) * 14);
            return (
              <div
                className={`grid grid-cols-2 ${desktopCols}`}
                style={{ gap: 14, maxWidth: gridMaxWidth, margin: "24px auto 0", justifyContent: "center" }}
                data-testid="problem-solution-proof-stats"
              >
                {shown.map((s, i) => (
                  <div
                    key={i}
                    style={{ backgroundColor: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, padding: "16px 12px", textAlign: "center" }}
                    data-testid={`problem-solution-proof-stat-${i}`}
                  >
                    {editableStored ? (
                      <>
                        <EditableText
                          value={s.n}
                          path={["psProof", "stats", i, "n"]}
                          as="p"
                          style={{ fontSize: 24, fontWeight: 900, color: HEADING, margin: "0 0 4px" }}
                          placeholder="4.8★"
                          testId={`text-problem-solution-stat-n-${i}`}
                        />
                        <EditableText
                          value={s.l}
                          path={["psProof", "stats", i, "l"]}
                          as="p"
                          style={{ fontSize: 12, color: MUTED, margin: 0 }}
                          placeholder="average rating"
                          testId={`text-problem-solution-stat-l-${i}`}
                        />
                      </>
                    ) : (
                      <>
                        <p style={{ fontSize: 24, fontWeight: 900, color: HEADING, margin: "0 0 4px" }} data-testid={`text-problem-solution-stat-n-${i}`}>
                          {s.n}
                        </p>
                        <p style={{ fontSize: 12, color: MUTED, margin: 0 }} data-testid={`text-problem-solution-stat-l-${i}`}>
                          {s.l}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 18, marginTop: 28 }}>
            {Array.from({
              length: Math.min(3, Math.max(quoteItems.length, reviewOptions.length)),
            }).map((_, i) => (
              <QuoteCard key={i} slot={i} item={quoteItems[i]} options={reviewOptions} />
            ))}
          </div>
        </div>
      </div>
    </EditableElement>
  );
}

// ── Offer box (prices DERIVED from real product/sale data) ──────────────────
function OfferSection({ content, ctx, headingFont, editor }: { content: LpProblemSolutionContent; ctx: ProblemSolutionCtx; headingFont: string; editor: boolean }) {
  const receipt = ctx.receipt;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  return (
    <div style={{ backgroundColor: PAGE_BG, padding: "64px 20px" }} data-testid="problem-solution-offer">
      <div
        style={{ maxWidth: 620, margin: "0 auto", textAlign: "center", border: `2px solid ${ACCENT}`, borderRadius: 18, padding: "40px 28px", backgroundColor: CARD }}
      >
        <EditableText
          value={content.offer.heading}
          path={["psOffer", "heading"]}
          as="h2"
          className={headingFont}
          style={{ fontSize: 28, fontWeight: 900, color: HEADING, lineHeight: 1.2, margin: "0 0 10px", letterSpacing: "-0.01em" }}
          placeholder="Try it today"
          testId="text-problem-solution-offer-heading"
        />
        {(content.offer.subhead || editor) && (
          <EditableText
            value={content.offer.subhead}
            path={["psOffer", "subhead"]}
            as="p"
            multiline
            style={{ fontSize: 16, lineHeight: 1.6, color: MUTED, margin: "0 0 18px" }}
            placeholder="One-line offer sub-head"
            testId="text-problem-solution-offer-subhead"
          />
        )}
        {/* Price — DERIVED from the receipt; strikethrough only with a real
            active discount. */}
        {receipt && (
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 10, margin: "0 0 20px" }}>
            <span style={{ fontSize: 32, fontWeight: 900, color: HEADING }} data-testid="text-problem-solution-price">
              {receipt.priceTodayLabel}
            </span>
            {receipt.hasDiscount && receipt.retailLabel && (
              <span style={{ fontSize: 18, color: "#999", textDecoration: "line-through" }}>{receipt.retailLabel}</span>
            )}
            {receipt.hasDiscount && receipt.discountPercent != null && (
              <span
                style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 800, fontSize: 11, padding: "3px 9px", borderRadius: 5, letterSpacing: "0.06em" }}
                data-testid="badge-problem-solution-save"
              >
                SAVE {receipt.discountPercent}%
              </span>
            )}
          </div>
        )}
        <CtaButton
          label={content.offer.ctaText}
          path={["psOffer", "ctaText"]}
          href={checkoutUrl}
          testId="button-problem-solution-cta"
        />
        {(content.offer.terms || editor) && (
          <EditableText
            value={content.offer.terms}
            path={["psOffer", "terms"]}
            as="p"
            style={{ fontSize: 12, color: MUTED, margin: "14px 0 0" }}
            placeholder="Free shipping · Easy returns"
            testId="text-problem-solution-offer-terms"
          />
        )}
      </div>
    </div>
  );
}

// ── Guarantee ────────────────────────────────────────────────────────────────
function GuaranteeSection({ content, headingFont, editor }: { content: LpProblemSolutionContent; headingFont: string; editor: boolean }) {
  if (!content.guarantee.heading && !content.guarantee.body && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, padding: "48px 20px" }}>
      <div
        style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 24, backgroundColor: CARD, border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: "28px 26px", flexWrap: "wrap" }}
        data-testid="problem-solution-guarantee"
      >
        <div
          style={{ width: 96, height: 96, borderRadius: "50%", backgroundColor: "rgba(45,122,79,0.1)", border: `2px solid ${ACCENT}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          <span style={{ fontSize: 22, fontWeight: 900, color: ACCENT, lineHeight: 1 }}>{content.guarantee.sealTop || "30"}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT, textTransform: "uppercase", letterSpacing: "0.06em" }}>{content.guarantee.sealBottom || "Day"}</span>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <EditableText
            value={content.guarantee.heading}
            path={["psGuarantee", "heading"]}
            as="h3"
            className={headingFont}
            style={{ fontSize: 21, fontWeight: 800, color: HEADING, margin: "0 0 8px" }}
            placeholder="Our guarantee"
            testId="text-problem-solution-guarantee-heading"
          />
          <EditableText
            value={content.guarantee.body}
            path={["psGuarantee", "body"]}
            as="p"
            multiline
            style={{ fontSize: 14, lineHeight: 1.7, color: TEXT, margin: 0 }}
            placeholder="Guarantee body"
            testId="text-problem-solution-guarantee-body"
          />
        </div>
      </div>
    </div>
  );
}

// ── FAQ accordion ────────────────────────────────────────────────────────────
function FaqItem({
  index,
  item,
  open,
  onToggle,
}: {
  index: number;
  item: { q: string; a: string };
  open: boolean;
  onToggle: () => void;
}) {
  const editor = useLpEditor();
  const expanded = editor || open;
  const base: (string | number)[] = ["psFaq", "items", index];
  return (
    <div style={{ borderBottom: `1px solid ${BORDER}` }} data-testid={`problem-solution-faq-${index}`}>
      <div
        onClick={editor ? undefined : onToggle}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "16px 0", cursor: editor ? "default" : "pointer" }}
      >
        <EditableText
          value={item.q}
          path={[...base, "q"]}
          as="span"
          style={{ fontSize: 16, fontWeight: 700, color: HEADING }}
          placeholder="Question"
          testId={`text-problem-solution-faq-q-${index}`}
        />
        {!editor && (
          <Plus
            style={{ width: 18, height: 18, color: MUTED, flexShrink: 0, transform: expanded ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}
          />
        )}
      </div>
      {expanded && (
        <EditableText
          value={item.a}
          path={[...base, "a"]}
          as="p"
          multiline
          style={{ fontSize: 14, lineHeight: 1.7, color: TEXT, margin: "0 0 16px" }}
          placeholder="Answer"
          testId={`text-problem-solution-faq-a-${index}`}
        />
      )}
    </div>
  );
}

function FaqSection({ content, headingFont, editor }: { content: LpProblemSolutionContent; headingFont: string; editor: boolean }) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  if (content.faq.items.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, padding: "56px 20px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SectionHeading
          value={content.faq.heading}
          path={["psFaq", "heading"]}
          headingFont={headingFont}
          placeholder="Frequently asked questions"
          testId="text-problem-solution-faq-heading"
        />
        <div style={{ marginTop: 22 }}>
          {content.faq.items.map((f, i) => (
            <FaqItem
              key={i}
              index={i}
              item={f}
              open={openFaq === i}
              onToggle={() => setOpenFaq((cur) => (cur === i ? null : i))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Disclaimer ───────────────────────────────────────────────────────────────
function DisclaimerSection({ content, editor }: { content: LpProblemSolutionContent; editor: boolean }) {
  if (!content.disclaimer && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, borderTop: `1px solid ${BORDER}`, padding: "24px 20px" }}>
      <EditableText
        value={content.disclaimer}
        path={["psDisclaimer", "text"]}
        as="p"
        multiline
        style={{ maxWidth: 760, margin: "0 auto", fontSize: 11, lineHeight: 1.6, color: MUTED, textAlign: "center" }}
        placeholder="Legal disclaimer"
        testId="text-problem-solution-disclaimer"
      />
    </div>
  );
}

// ── Sticky bottom CTA bar (mobile) ──────────────────────────────────────────
function StickyBar({ content, ctx }: { content: LpProblemSolutionContent; ctx: ProblemSolutionCtx }) {
  const editor = useLpEditor();
  const receipt = ctx.receipt;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  const bar = (
    <div
      className="lg:hidden"
      style={{ backgroundColor: DEEP, borderTop: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 -6px 24px rgba(0,0,0,0.25)" }}
      data-testid="problem-solution-sticky-bar"
    >
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "9px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          {receipt?.hasDiscount && receipt.discountPercent != null && (
            <span className="uppercase" style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 900, fontSize: 10, letterSpacing: "0.1em", padding: "2px 7px", borderRadius: 4 }}>
              {receipt.discountPercent}% OFF
            </span>
          )}
          {receipt && <span style={{ color: "#fff", fontWeight: 900, fontSize: 16, lineHeight: 1 }}>{receipt.priceTodayLabel}</span>}
          {receipt?.hasDiscount && receipt.retailLabel && (
            <span style={{ color: "rgba(255,255,255,0.4)", textDecoration: "line-through", fontSize: 11 }}>{receipt.retailLabel}</span>
          )}
        </div>
        {editor || !checkoutUrl ? (
          <span style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "9px 22px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap" }} data-lp-cta>
            <EditableText value={content.stickyCtaText} path={["psStickyCta", "ctaText"]} as="span" noHideDelete placeholder="Get started" testId="button-problem-solution-sticky-cta-label" />
          </span>
        ) : (
          <a href={checkoutUrl} style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "9px 22px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap", textDecoration: "none" }} data-testid="button-problem-solution-sticky-cta" data-lp-cta>
            {content.stickyCtaText}
          </a>
        )}
      </div>
    </div>
  );
  if (editor) return <div style={{ marginTop: 12 }}>{bar}</div>;
  return <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50 }}>{bar}</div>;
}

// ── Main ─────────────────────────────────────────────────────────────────────
// Renders ONE problem-solution section block, selected via `section`. All
// blocks read the whole-page copy shape assembled from the per-section
// generatedContent keys, and edit in place through the per-key paths.
export default function LpExpressProblemSolution({
  section,
  ctx,
}: {
  section: LpSectionType;
  ctx: ProblemSolutionCtx;
}) {
  const editor = !!useLpEditor();
  const content = assembleLpProblemSolutionContent(ctx.generatedContent);
  const headingFont = ctx.typography?.heading ?? "";
  const bodyFont = ctx.typography?.body ?? "";
  const wrap = (node: React.ReactNode) => <div className={bodyFont}>{node}</div>;

  switch (section) {
    case "psHero":
      return wrap(<HeroSection content={content} ctx={ctx} headingFont={headingFont} editor={editor} />);
    case "psProblem":
      return wrap(<ProblemSection content={content} headingFont={headingFont} editor={editor} />);
    case "psWhyFail":
      return wrap(<WhyFailSection content={content} headingFont={headingFont} editor={editor} />);
    case "psMechanism":
      return wrap(<MechanismSection content={content} ctx={ctx} headingFont={headingFont} editor={editor} />);
    case "psSolution":
      return wrap(<SolutionSection content={content} ctx={ctx} headingFont={headingFont} editor={editor} />);
    case "psProof":
      return wrap(<ProofSection content={content} ctx={ctx} headingFont={headingFont} />);
    case "psOffer":
      return wrap(<OfferSection content={content} ctx={ctx} headingFont={headingFont} editor={editor} />);
    case "psGuarantee":
      return wrap(<GuaranteeSection content={content} headingFont={headingFont} editor={editor} />);
    case "psFaq":
      return wrap(<FaqSection content={content} headingFont={headingFont} editor={editor} />);
    case "psDisclaimer":
      return wrap(<DisclaimerSection content={content} editor={editor} />);
    case "psStickyCta":
      return wrap(<StickyBar content={content} ctx={ctx} />);
    default:
      return null;
  }
}
